import { useState, useEffect } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { JiraProject } from '../__types__/apiUtils';
import type { IssueType } from '../providers/BulkCreateContextProvider';

import { fetchIssuesMetadata } from './apiUtils'; // Import the API function

export interface IssueMetadataField {
	key: string;
	required: boolean;
	hasDefaultValue: boolean;
	name?: string;
}

export type ShouldUseGicEmbedResult = boolean | null;

export interface UseShouldUseGicEmbedReturn {
	shouldUseEmbed: ShouldUseGicEmbedResult;
	isLoading: boolean;
	lightGicRequiredFields: IssueMetadataField[] | null;
	error: Error | null;
}

export interface UseShouldUseGicEmbedProps {
	selectedSite: AvailableSite | null;
	selectedProject: JiraProject | null;
	selectedIssueType: IssueType | null;
}

const defaultSupportedFields = ['summary', 'project', 'issuetype', 'description', 'reporter'];

/**
 * Hook to determine if GIC Anywhere should be used (true) or Light GIC (false)
 * based on metadata fetched for the provided site, project, and issue type.
 * Also returns loading and error states.
 */
export const useShouldUseGicEmbed = ({
	selectedSite,
	selectedProject,
	selectedIssueType,
}: UseShouldUseGicEmbedProps): UseShouldUseGicEmbedReturn => {
	const [shouldUseEmbed, setShouldUseEmbed] = useState<ShouldUseGicEmbedResult>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);
	const [lightGicRequiredFields, setLightGicRequiredFields] = useState<IssueMetadataField[] | null>(
		null,
	);

	useEffect(() => {
		if (!selectedSite || !selectedProject || !selectedIssueType) {
			setShouldUseEmbed(null); // Undetermined if essential inputs are missing
			setIsLoading(false);
			setError(null);
			setLightGicRequiredFields(null); // Reset
			return;
		}

		const cloudId = selectedSite.cloudId;
		const projectId = selectedProject.id;
		const issueTypeId = selectedIssueType.id;

		const determineMode = async () => {
			setIsLoading(true);
			setError(null);
			setShouldUseEmbed(null);
			setLightGicRequiredFields(null); // Reset

			try {
				const metadata = await fetchIssuesMetadata({
					cloudId,
					projectId,
					issueTypeId,
				});

				if (metadata && metadata.fields && metadata.fields.length > 0) {
					const requiredFields = metadata.fields.filter(
						(field: IssueMetadataField) =>
							field.required &&
							!defaultSupportedFields.includes(field.key) &&
							!field.hasDefaultValue,
					);

					if (requiredFields.length > 0) {
						setShouldUseEmbed(true); // Should use GIC Anywhere (not Light GIC)
						setLightGicRequiredFields(requiredFields);
					} else {
						setShouldUseEmbed(false); // Should use Light GIC
						const supportedFields = metadata.fields.filter(
							(field: IssueMetadataField) =>
								field.required && defaultSupportedFields.includes(field.key),
						);
						setLightGicRequiredFields(supportedFields);
					}
				} else {
					// Metadata is empty but no error, this is likely error case
					// Fall back to Gic Embed
					setShouldUseEmbed(true);
					setLightGicRequiredFields(null);
				}
			} catch (e: Error | unknown) {
				setError(e instanceof Error ? e : new Error('Failed to fetch issue metadata in Light GIC'));
				setShouldUseEmbed(false);
				setLightGicRequiredFields(null);
			} finally {
				setIsLoading(false);
			}
		};

		void determineMode();
	}, [selectedSite, selectedProject, selectedIssueType]);

	return { shouldUseEmbed, isLoading, lightGicRequiredFields, error };
};
